import { useEffect } from 'react'
import * as Icons from 'react-icons/pi'
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import Logo from '../assets/svg/NEMA.svg'
import styles from './AdminLayout.module.css'

const navIcons = {
  sidebar: Icons.PiSidebarSimpleFill,
  blog: Icons.PiNotePencilFill,
  bookings: Icons.PiCalendarCheckFill,
  enquiries: Icons.PiEnvelopeSimpleFill,
  credits: Icons.PiCoinsFill,
  accounts: Icons.PiUserFill,
  invoices: Icons.PiFileTextFill,
}

// Flat navigation structure with type keys
const navigation = [
  { type: 'title', label: 'Management' },
  { type: 'link', to: '/admin/credits', icon: 'credits', label: 'Licenses', roles: ['admin'] },
  { type: 'link', to: '/admin/accounts', icon: 'accounts', label: 'Accounts', roles: ['admin'] },
  { type: 'divider' },
]

// Sub-components for different navigation types
const Title = ({ label }) => <div className={styles.navGroupTitle}>{label}</div>

const LinkItem = ({ to, icon, label }) => {
  const IconComponent = navIcons[icon]
  return (
    <NavLink to={to} className={({ isActive }) => `${styles.navItem} ${isActive ? styles.navItemActive : ''}`}>
      <IconComponent className={styles.navIcon} />
      <span>{label}</span>
    </NavLink>
  )
}

const Divider = () => <div className={styles.navDivider}></div>

const AdminLayout = () => {
  const user = JSON.parse(localStorage.getItem('user') || null)

  const navigate = useNavigate()

  const filtered = navigation
    // keep only allowed links
    .filter((item) => item.type !== 'link' || !item.roles || item.roles.includes(user?.role))
    // drop orphan titles
    .filter((item, idx, arr) => {
      if (item.type !== 'title') return true
      // look ahead until next title or divider
      for (let i = idx + 1; i < arr.length; i++) {
        if (arr[i].type === 'link') return true
        if (arr[i].type === 'title' || arr[i].type === 'divider') return false
      }
      return false
    })
    // clean dividers
    .filter((item, idx, arr) => {
      if (item.type !== 'divider') return true
      if (idx === 0 || idx === arr.length - 1) return false
      return arr[idx - 1].type !== 'divider' && arr[idx + 1].type !== 'divider'
    })

  useEffect(() => {
    const aswContainer = document.getElementsByClassName('asw-container')[0]

    if (aswContainer) {
      aswContainer.style.setProperty('display', 'none', 'important')
    }
    return () => {
      if (aswContainer) {
        aswContainer.style.setProperty('display', 'block', 'important')
      }
    }
  }, [])

  useEffect(() => {
    const redirectTo = filtered.find((item) => item.type === 'link')?.to
    navigate(redirectTo)
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('user')
    localStorage.removeItem('token')
    navigate('/admin/login')
  }

  const renderNavItem = (item, index) => {
    switch (item.type) {
      case 'title':
        return <Title key={index} label={item.label} />
      case 'link':
        return <LinkItem key={index} to={item.to} icon={item.icon} label={item.label} />
      case 'divider':
        return <Divider key={index} />
      default:
        return null
    }
  }

  return (
    <div className={styles.container}>
      <aside className={styles.sidebar}>
        <div className={styles.sidebarContent}>
          <div className={styles.navBrand}>
            <Link to='/'>
              <img src={Logo} alt='Logo' className={styles.brandLogo} />
            </Link>
            <button className={styles.sidebarToggleBtn}>
              <Icons.PiSidebarSimpleFill className={styles.navIconToggle} />
            </button>
          </div>
          <nav className={styles.nav} aria-label='Admin Navigation'>
            <div className={styles.navList}>{filtered.map((item, index) => renderNavItem(item, index))}</div>
          </nav>

          <div className={styles.footer}>
            <div className={styles.userInfo}>
              <div className={styles.avatar}>
                <img src='https://placehold.co/40x40/6b7280/ffffff?text=AD' alt='Admin Avatar' />
              </div>
              <div className={styles.userDetails}>
                <div className={styles.userEmail}>{user?.email_id}</div>
              </div>
              <button className={styles.logoutBtn} onClick={handleLogout}>
                <Icons.PiSignOutFill className={styles.logoutIcon} />
              </button>
            </div>
          </div>
        </div>
      </aside>
      <main className={styles.main}>
        <div className={styles.mainContainer}>
          <Outlet />
        </div>
      </main>
    </div>
  )
}

export default AdminLayout
